import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import React, { useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { months, monthToNumber } from "../../../handlers/utilityFunctions";
import { getJobFunctions } from "../../../seed/getMetadata";
import { useUserAccount } from "../../../services/create-resume/queries";
import { ResumeDataService } from "../../../services/create-resume/resume-data";
import Dropdown from "../components/Dropdown";
import OptionBadge from "../components/OptionBadge";
import AuthLayout from "../layout/AuthLayout";
import { clearPendingSignup, getPendingSignup, PendingSignup, setPendingSignup, YesNo } from "../pendingSignup";
import { deriveSeniorityLevel } from "../seniority";

const MAX_JOB_TITLE_LENGTH = 100;

/** Graduating soon counts too, so the window runs a couple of years into the future. */
function graduationYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = thisYear - 3; year <= thisYear + 2; year++) years.push(String(year));
	return years.reverse();
}

function careerStartYears(): string[] {
	const thisYear = new Date().getFullYear();
	const years = [];
	for (let year = 1980; year <= thisYear; year++) years.push(String(year));
	return years.reverse();
}

function OnboardingScreen() {
	const navigate = useNavigate();
	const pendingSignup = useMemo(() => getPendingSignup(), []);
	const api = useMemo(() => new ResumeDataService(), []);
	const queryClient = useQueryClient();
	const [submitting, setSubmitting] = React.useState(false);
	// Synchronous guard: `submitting` state only disables the button on the next
	// render, so a fast double-submit could fire two POSTs before that lands.
	const isCreatingRef = useRef(false);

	const [firstName, setFirstName] = React.useState("");
	const [lastName, setLastName] = React.useState("");
	const [jobTitle, setJobTitle] = React.useState("");
	const [areasOfExpertise, setAreasOfExpertise] = React.useState<string[]>([]);
	const [recentGraduate, setRecentGraduate] = React.useState<YesNo | "">("");
	const [experienceMonth, setExperienceMonth] = React.useState("");
	const [experienceYear, setExperienceYear] = React.useState("");

	// A draft rebuilt at sign-in for an account whose record went missing carries no name — Cognito
	// only ever stores the email address. POST /users requires both, so ask for them here rather than
	// sending the user through a second screen. Derived from the stored draft, not from state, so the
	// fields don't vanish while they're being filled in.
	const needsName = Boolean(pendingSignup) && !(pendingSignup.firstName?.trim() && pendingSignup.lastName?.trim());

	const answeredQuestions = useRef<Set<string>>(new Set());

	// A returning user signing back in with Google is sent here by the register form, which can't
	// tell an existing account from a new one. Disabled without an auth token, so the email-signup
	// path (not logged in until the address is verified) never waits on it.
	const { data: userAccount, isLoading: isLoadingAccount } = useUserAccount();
	const preferences = userAccount?.jobPreferences;
	const hasAnsweredOnboarding = Boolean(
		preferences?.desiredTitle || preferences?.areasOfExpertise?.length > 0 || preferences?.seniorityLevel,
	);

	function trackQuestionAnswered(question: string) {
		if (answeredQuestions.current.has(question)) return;
		answeredQuestions.current.add(question);
		posthog.capture("registration_question_answered", { question, step: "onboarding" });
	}

	useEffect(() => {
		if (!pendingSignup) return;
		posthog.capture("registration_onboarding_started", { method: pendingSignup.method });
	}, [pendingSignup]);

	// Rehydrate if the user came back to this step.
	useEffect(() => {
		if (!pendingSignup) return;
		setFirstName(pendingSignup.firstName || "");
		setLastName(pendingSignup.lastName || "");
		setJobTitle(pendingSignup.jobTitle || "");
		setAreasOfExpertise(pendingSignup.areasOfExpertise || []);
		setRecentGraduate(pendingSignup.recentGraduate || "");
		setExperienceMonth(pendingSignup.experienceMonth || "");
		setExperienceYear(pendingSignup.experienceYear || "");
	}, [pendingSignup]);

	// Don't flash the questions at someone who is about to be bounced past them.
	if (isLoadingAccount) {
		return null;
	}

	if (hasAnsweredOnboarding) {
		// The register form wrote a draft on the way in; it would otherwise re-POST /users later.
		clearPendingSignup();
		return <Navigate to="/profile" replace />;
	}

	if (!pendingSignup) {
		return <Navigate to="/register" replace />;
	}

	// Create the backend record from the collected draft and land on the profile.
	// Used for accounts that are already signed in (Google, or an email account
	// whose link was already followed), so the record exists before they reach a
	// page that requires it.
	function createAccountAndGoToProfile(draft: PendingSignup) {
		if (isCreatingRef.current) return;
		isCreatingRef.current = true;
		setSubmitting(true);

		// Persist the answers before the request so they survive a refresh — or the
		// response interceptor redirecting on an auth failure — and are cleared only
		// once the record exists.
		setPendingSignup(draft);

		const experienceDate =
			draft.experienceMonth && draft.experienceYear
				? { month: monthToNumber(draft.experienceMonth), year: Number(draft.experienceYear) }
				: null;
		const experienceKey = draft.recentGraduate === "Yes" ? "graduationDate" : "professionalSince";

		// Only the keys the backend whitelists — it rejects unknown fields.
		const data = {
			emailAddress: draft.email,
			firstName: draft.firstName,
			lastName: draft.lastName,
			...(draft.phone ? { phoneNumber: draft.phone } : {}),
			jobPreferences: {
				desiredTitle: draft.jobTitle,
				areasOfExpertise: draft.areasOfExpertise || [],
				seniorityLevel: draft.seniorityLevel,
				...(draft.recentGraduate ? { recentGraduate: draft.recentGraduate } : {}),
				...(draft.recentGraduate && experienceDate ? { [experienceKey]: experienceDate } : {}),
			},
		};

		api.createUserAccount(
			data,
			(response: { data: unknown }) => {
				localStorage.setItem("jobPreferences", JSON.stringify(response.data));
				clearPendingSignup();
				// A stale anonymous account read must not outlive the sign-in that just happened.
				queryClient.invalidateQueries({ queryKey: ["user", "account"] });
				navigate("/profile");
			},
			(error: { response?: { data?: { message?: string | string[] } } }) => {
				isCreatingRef.current = false;
				setSubmitting(false);
				// class-validator returns `message` as a string[] for validation failures.
				const message = error?.response?.data?.message;
				toast.error(
					(Array.isArray(message) ? message.join(". ") : message) ||
						"We couldn't finish setting up your profile. Please try again.",
				);
			},
		);
	}

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// A second submit that lands before the button re-renders as disabled would otherwise
		// re-run the validation and the "completed" analytics event.
		if (isCreatingRef.current) return;

		if (needsName && !(firstName.trim() && lastName.trim())) {
			toast.error("Please enter your first and last name");
			return;
		}

		if (!jobTitle.trim()) {
			toast.error("Please enter your desired job title");
			return;
		}

		if (areasOfExpertise.length === 0) {
			toast.error("Please select your area of expertise");
			return;
		}

		if (!recentGraduate) {
			toast.error("Please let us know if you are a recent graduate");
			return;
		}

		if (!experienceMonth || !experienceYear) {
			toast.error(
				recentGraduate === "Yes"
					? "Please select when you graduated or expect to graduate"
					: "Please select when you started your professional career",
			);
			return;
		}

		const seniorityLevel = deriveSeniorityLevel(monthToNumber(experienceMonth), Number(experienceYear));

		const draft: PendingSignup = {
			...pendingSignup,
			firstName: firstName.trim(),
			lastName: lastName.trim(),
			jobTitle: jobTitle.trim(),
			areasOfExpertise,
			recentGraduate,
			experienceMonth,
			experienceYear,
			seniorityLevel,
		};

		posthog.capture("registration_onboarding_completed", {
			method: draft.method,
			recent_graduate: recentGraduate,
			// The band isn't asked for any more, so record what we inferred.
			derived_seniority_level: seniorityLevel,
		});

		// An email account is not signed in until its verification link is followed, so it finishes
		// through /creating-profile, which creates the record and shows the "check your email" screen.
		// A Google (or already-verified) account is signed in now: create the record here and go
		// straight to the profile, so there is no step where a signed-in user has no record yet.
		if (draft.method === "google" || draft.emailVerified) {
			createAccountAndGoToProfile(draft);
		} else {
			setPendingSignup(draft);
			navigate("/creating-profile", { state: { method: draft.method } });
		}
	};

	return (
		<AuthLayout>
			<div className="py-12">
				<div className="mb-6 flex flex-col items-center space-y-2">
					<h3 className="font-bold! text-[28px] leading-tight text-[#161616] font-plus-jakarta! mb-2 text-center">
						Welcome{pendingSignup.firstName ? `, ${pendingSignup.firstName}` : ""}!
					</h3>
					<p className="text-[#5C5C5C] text-sm text-center">
						{needsName
							? "Let's finish setting up your account so we can start matching you with the right jobs."
							: "Three quick questions so we can start matching you with the right jobs."}
					</p>
				</div>

				<form onSubmit={onSubmit}>
					{needsName && (
						<div className="grid grid-cols-2 gap-3 mb-4">
							<div className="flex flex-col gap-1">
								<label className="font-semibold text-sm text-gray-700">First name</label>
								<input
									type="text"
									className="border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
									placeholder="Enter your first name"
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									onBlur={(e) => {
										if (e.target.value.trim()) trackQuestionAnswered("first_name");
									}}
								/>
							</div>
							<div className="flex flex-col gap-1">
								<label className="font-semibold text-sm text-gray-700">Last name</label>
								<input
									type="text"
									className="border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
									placeholder="Enter your last name"
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									onBlur={(e) => {
										if (e.target.value.trim()) trackQuestionAnswered("last_name");
									}}
								/>
							</div>
						</div>
					)}

					<div className="flex flex-col gap-1 mb-4">
						<label className="font-semibold text-sm text-gray-700">What&#39;s your dream job title?</label>
						<input
							type="text"
							className="border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
							placeholder="Enter your desired job title"
							maxLength={MAX_JOB_TITLE_LENGTH}
							value={jobTitle}
							onChange={(e) => setJobTitle(e.target.value)}
							onBlur={(e) => {
								if (e.target.value.trim()) trackQuestionAnswered("job_title");
							}}
						/>
					</div>

					<div className="flex flex-col gap-1 mb-4">
						<label className="font-semibold text-sm text-gray-700">
							What&#39;s your superpower skill or area of expertise?
						</label>
						<Dropdown
							placeholder="Select all that apply"
							multiple
							options={[...getJobFunctions()].sort()}
							onChange={(val) => {
								if (areasOfExpertise.includes(val)) {
									setAreasOfExpertise(areasOfExpertise.filter((item) => item !== val));
								} else {
									setAreasOfExpertise([...areasOfExpertise, val]);
									trackQuestionAnswered("area_of_expertise");
								}
							}}
							value={areasOfExpertise}
						/>
					</div>

					<div className="flex flex-col gap-1 mb-6">
						<label className="font-semibold text-sm text-gray-700">Are you a recent graduate?</label>
						<div className="flex gap-3 mt-1 flex-wrap">
							{(["Yes", "No"] as YesNo[]).map((answer) => (
								<OptionBadge
									key={answer}
									value={answer}
									selected={recentGraduate === answer}
									onSelect={() => {
										if (answer !== recentGraduate) {
											// The two branches ask about different dates — don't carry one answer into the other.
											setExperienceMonth("");
											setExperienceYear("");
										}
										setRecentGraduate(answer);
										trackQuestionAnswered("recent_graduate");
									}}
								/>
							))}
						</div>

						{recentGraduate && (
							<>
								<label className="font-semibold text-sm text-gray-700 mt-3">
									{recentGraduate === "Yes"
										? "When did you graduate or expect to graduate?"
										: "When did you start your professional career?"}
								</label>
								<div className="grid grid-cols-2 gap-3">
									<Dropdown
										placeholder="Select Month"
										options={months}
										value={experienceMonth}
										onChange={(val) => {
											setExperienceMonth(val);
											trackQuestionAnswered(recentGraduate === "Yes" ? "graduation_date" : "career_start");
										}}
									/>
									<Dropdown
										placeholder="Select Year"
										options={recentGraduate === "Yes" ? graduationYears() : careerStartYears()}
										value={experienceYear}
										onChange={(val) => {
											setExperienceYear(val);
											trackQuestionAnswered(recentGraduate === "Yes" ? "graduation_date" : "career_start");
										}}
									/>
								</div>
							</>
						)}
					</div>

					<button
						className="w-full rounded-md bg-primary-700 text-white font-semibold font-inter text-base py-2 flex items-center justify-center disabled:opacity-60"
						type="submit"
						disabled={submitting}
					>
						{submitting ? "Setting up your profile…" : "Continue"}
					</button>
				</form>
			</div>
		</AuthLayout>
	);
}

export default OnboardingScreen;
